/**
 * RP80 → RP83 — « Ma semaine » : libellés, liens et lignes communs au tableau de bord
 * de l'architecte (pages/Dashboard.jsx), à la page « Ma semaine » du menu
 * (pages/MesTaches.jsx) et au tableau de bord partenaire (DashboardIntervenant.jsx).
 * Données : GET /api/me/ma-semaine (limitées aux chantiers de la personne).
 */
import { libelleCompteRendu, LOTS_PARTENAIRE } from '../../lib/libelles';

export const URL_MA_SEMAINE = '/api/me/ma-semaine';

export const dateCourte = (iso) => {
  if (!iso) return '';
  const s = String(iso);
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

export const heureCourte = (iso) => {
  const s = String(iso || '');
  return s.length >= 16 && s[10] === 'T' ? s.slice(11, 16) : '';
};

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

export const lienChantier = (cid, onglet, extra = '') => `/chantiers/${cid}?tab=${onglet}${extra}`;

// Chaque bloc : titre visible, lien d'une ligne, texte principal et détail.
export const BLOCS = {
  // Fluidité B5 — propositions à trier dans le Fil (une ligne par chantier)
  a_trier: {
    titre: 'À trier',
    lien: (x) => lienChantier(x.chantier_id, 'messages', '&filtre=a_trier'),
    texte: (x) => pluriel(x.nombre || 0, 'proposition à trier', 'propositions à trier'),
    detail: (x) => (x.plus_ancienne ? `la plus ancienne du ${dateCourte(x.plus_ancienne)}` : ''),
  },
  actions_en_retard: {
    titre: 'Actions en retard',
    lien: (x) => lienChantier(x.chantier_id, 'taches'),
    texte: (x) => x.titre,
    detail: (x) => [x.entreprise, `échéance ${dateCourte(x.echeance)}`,
      x.jours_retard ? `${pluriel(x.jours_retard, 'jour', 'jours')} de retard` : '',
      // B3 — « à qui la balle » quand l'architecte doit répondre
      x.balle && x.balle.includes('vous') ? x.balle : ''].filter(Boolean).join(' · '),
    alerte: true,
  },
  levees_a_verifier: {
    titre: 'Levées à vérifier',
    // Fluidité A3 (constat 24) — ouvre directement le filtre « À vérifier » (avant : « À faire »).
    lien: (x) => lienChantier(x.chantier_id, 'taches', '&etat=a_verifier'),
    texte: (x) => x.titre,
    detail: (x) => [x.signalee_par || x.entreprise, x.signalee_le ? `signalée le ${dateCourte(x.signalee_le)}` : '',
      x.avec_photo ? 'avec photo' : ''].filter(Boolean).join(' · '),
  },
  cr_a_relire: {
    titre: 'Comptes rendus à relire',
    lien: (x) => lienChantier(x.chantier_id, 'suivi', '&sub=crc'),
    texte: (x) => libelleCompteRendu(x.numero),
    detail: (x) => [x.soumis_relecture_par_nom ? `soumis par ${x.soumis_relecture_par_nom}` : '',
      x.soumis_relecture_at ? `le ${dateCourte(x.soumis_relecture_at)}` : ''].filter(Boolean).join(' '),
  },
  // A2 (constat 23) — désaccord d'une entreprise sur un point d'un CR diffusé
  desaccords: {
    titre: 'Désaccords des entreprises',
    lien: (x) => lienChantier(x.chantier_id, 'suivi', '&sub=crc'),
    texte: (x) => `${x.entreprise || 'Entreprise'} — ${x.point_libelle || libelleCompteRendu(x.numero)}`,
    detail: (x) => [x.point_numero ? `${libelleCompteRendu(x.numero)}, point n° ${x.point_numero}` : libelleCompteRendu(x.numero),
      x.commentaire ? `« ${x.commentaire} »` : '', x.date ? `le ${dateCourte(x.date)}` : ''].filter(Boolean).join(' · '),
    alerte: true,
  },
  // B3 — « Pas d'accord » et « Question » d'une entreprise sur une action (lien « Vos actions »)
  reponses_entreprises: {
    titre: 'Réponses des entreprises',
    lien: (x) => lienChantier(x.chantier_id, 'taches', `&element_id=${x.element_id}`),
    texte: (x) => `${x.entreprise || 'Entreprise'} — ${x.type_libelle} : ${x.titre}`,
    detail: (x) => [x.texte ? `« ${x.texte} »` : '', x.date ? `le ${dateCourte(x.date)}` : ''].filter(Boolean).join(' · '),
    alerte: true,
  },
  cr_soumis: {
    titre: "Soumis à l'architecte",
    lien: (x) => lienChantier(x.chantier_id, 'suivi', '&sub=crc'),
    texte: (x) => libelleCompteRendu(x.numero),
    detail: (x) => (x.soumis_relecture_at ? `soumis le ${dateCourte(x.soumis_relecture_at)} · en attente de relecture` : 'en attente de relecture'),
  },
  cr_brouillons: {
    titre: 'Brouillons en cours',
    lien: (x) => lienChantier(x.chantier_id, 'suivi', '&sub=crc'),
    texte: (x) => `${libelleCompteRendu(x.numero)} — brouillon`,
    detail: (x) => (x.date ? `commencé le ${dateCourte(x.date)}` : ''),
  },
  reunions_sans_cr: {
    titre: 'Réunions sans compte rendu',
    lien: (x) => lienChantier(x.chantier_id, 'suivi', '&sub=reunions'),
    texte: (x) => x.titre,
    detail: (x) => `le ${dateCourte(x.date_debut)} · compte rendu à rédiger`,
  },
  reunions: {
    titre: 'Réunions de la semaine',
    lien: (x) => lienChantier(x.chantier_id, 'suivi', '&sub=reunions'),
    texte: (x) => x.titre,
    detail: (x) => [dateCourte(x.date_debut), heureCourte(x.date_debut), x.lieu].filter(Boolean).join(' · '),
  },
  visas_en_retard: {
    titre: 'Visas en retard',
    lien: (x) => lienChantier(x.chantier_id, 'visas', `&visa=${x.id}`),
    texte: (x) => `${x.titre || 'Pièce'} — ${x.piece_libelle || ''}`,
    detail: (x) => [x.lot_code, x.viseur_nom ? `viseur : ${x.viseur_nom}` : '',
      `date limite ${dateCourte(x.date_limite)}`].filter(Boolean).join(' · '),
    alerte: true,
  },
  visas_a_viser: {
    titre: 'À viser',
    lien: (x) => lienChantier(x.chantier_id, 'visas', `&visa=${x.id}`),
    texte: (x) => `${x.titre || 'Pièce'} — ${x.piece_libelle || ''}`,
    detail: (x) => [x.lot_code, x.entreprise_nom, `date limite ${dateCourte(x.date_limite)}`,
      x.en_retard ? 'en retard' : ''].filter(Boolean).join(' · '),
  },
  lots_a_relire: {
    titre: LOTS_PARTENAIRE.aRedigerARelire, // Fluidité B2 (P3#30) — ex-« Lots confiés à relire »
    lien: (x) => lienChantier(x.chantier_id, 'conception'),
    texte: (x) => x.lot_nom,
    detail: (x) => [x.user_nom, x.statut_libelle].filter(Boolean).join(' · '),
  },
  lots_confies: {
    titre: LOTS_PARTENAIRE.aRediger, // Fluidité B2 (P3#30) — ex-« Lots confiés »
    lien: (x) => lienChantier(x.chantier_id, 'conception'),
    texte: (x) => x.lot_nom,
    detail: (x) => x.statut_libelle,
  },
  relances: {
    titre: 'Relances à faire',
    lien: (x) => (x.type === 'consultation' ? lienChantier(x.chantier_id, 'marches') : lienChantier(x.chantier_id, 'taches')),
    texte: (x) => x.entreprise,
    detail: (x) => (x.type === 'consultation'
      ? `pas de réponse à la consultation${(x.lots || []).length ? ` (${x.lots.join(', ')})` : ''} · date limite ${dateCourte(x.date_limite)}`
      : `${pluriel(x.nb_actions, 'action en retard', 'actions en retard')} depuis le ${dateCourte(x.plus_ancienne_echeance)}`),
  },
};

// Colonnes de la vue par chantier (RP81).
export const COLONNES_PAR_CHANTIER = [
  { cle: 'a_trier', libelle: 'À trier' },
  { cle: 'actions_en_retard', libelle: 'Actions en retard' },
  { cle: 'levees_a_verifier', libelle: 'Levées à vérifier' },
  { cle: 'cr_a_relire', libelle: 'CR à relire' },
  { cle: 'desaccords', libelle: 'Désaccords' },
  { cle: 'reponses_entreprises', libelle: 'Réponses' },
  { cle: 'reunions', libelle: 'Réunions' },
  { cle: 'visas_en_retard', libelle: 'Visas en retard' },
  { cle: 'lots_a_relire', libelle: 'Lots à relire' },
];
